import { useEffect, useState } from 'react';
import { FiCheck, FiCopy, FiCreditCard, FiExternalLink } from 'react-icons/fi';
import { api } from '../../../services/api';
import { Modal } from '../Modal/Modal';
import styles from './PaymentChargeModal.module.css';

type Charge = Awaited<ReturnType<typeof api.getMyCurrentCharge>>;

interface PaymentChargeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SUPPORT_URL =
    'https://api.whatsapp.com/send/?phone=558587069521&text=Oi%2C+preciso+de+ajuda+com+o+pagamento+da+minha+assinatura%21&type=phone_number&app_absent=0';

function formatDate(iso?: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function PaymentChargeModal({ isOpen, onClose }: PaymentChargeModalProps) {
    const [loading, setLoading] = useState(false);
    const [charge, setCharge] = useState<Charge | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);
        setCharge(null);
        setCopied(false);
        api.getMyCurrentCharge()
            .then(setCharge)
            .catch((err) => setError(err?.message || 'Não foi possível carregar a cobrança.'))
            .finally(() => setLoading(false));
    }, [isOpen]);

    const handleCopy = async () => {
        if (!charge?.pix?.copyPaste) return;
        try {
            await navigator.clipboard.writeText(charge.pix.copyPaste);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // clipboard pode falhar em contexto não seguro — ignora
        }
    };

    const renderBody = () => {
        if (loading) {
            return <p className={styles.muted}>Carregando cobrança…</p>;
        }
        if (error) {
            return (
                <div className={styles.center}>
                    <p className={styles.muted}>{error}</p>
                    <a className={styles.supportBtn} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                        <FiExternalLink size={16} /> Falar com o Suporte
                    </a>
                </div>
            );
        }
        if (!charge) return null;

        // PIX — exibe QR + copia-e-cola
        if (charge.paymentMethod === 'pix' && charge.pix?.copyPaste) {
            return (
                <div className={styles.pixBox}>
                    {charge.amount != null && (
                        <p className={styles.amount}>
                            {charge.amount.toLocaleString('pt-BR', { style: 'currency', currency: charge.currency || 'BRL' })}
                        </p>
                    )}
                    {charge.pix.qrCode && (
                        <img className={styles.qr} src={charge.pix.qrCode} alt="QR Code PIX" />
                    )}
                    <label className={styles.label}>PIX copia e cola</label>
                    <div className={styles.copyRow}>
                        <input className={styles.copyInput} readOnly value={charge.pix.copyPaste} />
                        <button className={styles.copyBtn} onClick={handleCopy}>
                            {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
                            {copied ? 'Copiado' : 'Copiar'}
                        </button>
                    </div>
                    {charge.pix.expiresAt && (
                        <p className={styles.muted}>Válido até {formatDate(charge.pix.expiresAt)}.</p>
                    )}
                    <p className={styles.hint}>
                        Após o pagamento, seu acesso é liberado automaticamente em alguns instantes.
                    </p>
                </div>
            );
        }

        // Cartão — cobrança automática, nada a gerar
        if (charge.paymentMethod === 'card') {
            return (
                <div className={styles.center}>
                    <span className={styles.cardIcon}><FiCreditCard size={32} /></span>
                    <p className={styles.cardText}>
                        Sua assinatura é cobrada <strong>automaticamente no cartão</strong>
                        {charge.card?.last4 ? ` final ${charge.card.last4}` : ''}.
                    </p>
                    {charge.nextBillingDate && (
                        <p className={styles.muted}>Próxima cobrança em {formatDate(charge.nextBillingDate)}.</p>
                    )}
                    <p className={styles.hint}>
                        Se houver pendência, atualize os dados do cartão ou fale com o suporte.
                    </p>
                    <a className={styles.supportBtn} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                        <FiExternalLink size={16} /> Falar com o Suporte
                    </a>
                </div>
            );
        }

        // Sem cobrança disponível
        return (
            <div className={styles.center}>
                <p className={styles.muted}>
                    Não há cobrança disponível no momento. Verifique o link de pagamento enviado por e-mail
                    ou fale com o suporte.
                </p>
                <a className={styles.supportBtn} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                    <FiExternalLink size={16} /> Falar com o Suporte
                </a>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Pagamento da assinatura" size="small">
            {renderBody()}
        </Modal>
    );
}
